import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import CabinetColumnCustomizerDraggableRow, { FieldSwitch, fieldRowHandleStyle, fieldRowLabelStyle, fieldRowLayoutClass } from './cabinetColumnCustomizerDraggableRow';
const reorderList = (list, fromIndex, toIndex) => {
  const next = [...list];
  const _next$splice = next.splice(fromIndex, 1),
    _next$splice2 = _slicedToArray(_next$splice, 1),
    removed = _next$splice2[0];
  next.splice(toIndex, 0, removed);
  return next;
};
const matchesSearch = (title, searchTerm) => {
  if (!searchTerm) {
    return true;
  }
  return title.toLowerCase().includes(searchTerm.toLowerCase());
};
const StaticFieldRow = _ref => {
  let title = _ref.title;
  const safeTitle = title.replace(/[^A-Za-z0-9_-]/g, '-');
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(fieldRowLayoutClass, " text-muted"),
    "data-testid": "t-col-customizer-static-".concat(safeTitle)
  }, /*#__PURE__*/React.createElement("div", {
    style: fieldRowLabelStyle
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    style: fieldRowHandleStyle
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-truncate"
  }, title)), /*#__PURE__*/React.createElement(FieldSwitch, {
    id: "t-col-customizer-static-switch-".concat(safeTitle),
    checked: true,
    disabled: true
  }));
};
StaticFieldRow.propTypes = {
  title: PropTypes.string.isRequired
};
const HiddenFieldRow = _ref2 => {
  let field = _ref2.field,
    onToggleOn = _ref2.onToggleOn;
  return /*#__PURE__*/React.createElement("div", {
    className: fieldRowLayoutClass,
    "data-testid": "t-col-customizer-hidden-".concat(field.key)
  }, /*#__PURE__*/React.createElement("div", {
    style: fieldRowLabelStyle
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    style: fieldRowHandleStyle
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-truncate"
  }, field.title)), /*#__PURE__*/React.createElement(FieldSwitch, {
    id: "t-col-customizer-hidden-switch-".concat(field.key),
    checked: false,
    onChange: () => onToggleOn(field.key)
  }));
};
HiddenFieldRow.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  onToggleOn: PropTypes.func.isRequired
};
const CabinetColumnCustomizer = _ref3 => {
  let isOpen = _ref3.isOpen,
    staticColumns = _ref3.staticColumns,
    candidateFields = _ref3.candidateFields,
    addedColumnIndexes = _ref3.addedColumnIndexes,
    onSave = _ref3.onSave,
    onClose = _ref3.onClose;
  const _useState = useState(addedColumnIndexes),
    _useState2 = _slicedToArray(_useState, 2),
    pendingIndexes = _useState2[0],
    setPendingIndexes = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    searchTerm = _useState4[0],
    setSearchTerm = _useState4[1];
  useEffect(() => {
    if (isOpen) {
      setPendingIndexes(addedColumnIndexes);
      setSearchTerm('');
    }
  }, [isOpen, addedColumnIndexes]);
  const fieldByKey = useMemo(() => {
    const map = {};
    candidateFields.forEach(field => {
      map[field.key] = field;
    });
    return map;
  }, [candidateFields]);
  const hiddenFields = useMemo(() => candidateFields.filter(f => !pendingIndexes.includes(f.key)), [candidateFields, pendingIndexes]);
  const filteredStaticColumns = useMemo(() => staticColumns.filter(col => matchesSearch(col.title || '', searchTerm)), [staticColumns, searchTerm]);
  const filteredPendingKeys = useMemo(() => pendingIndexes.filter(key => {
    var _fieldByKey$key;
    const title = ((_fieldByKey$key = fieldByKey[key]) === null || _fieldByKey$key === void 0 ? void 0 : _fieldByKey$key.title) || key;
    return matchesSearch(title, searchTerm);
  }), [pendingIndexes, fieldByKey, searchTerm]);
  const filteredHiddenFields = useMemo(() => hiddenFields.filter(f => matchesSearch(f.title, searchTerm)), [hiddenFields, searchTerm]);
  const handleToggleOn = key => {
    setPendingIndexes(prev => prev.includes(key) ? prev : [...prev, key]);
  };
  const handleToggleOff = key => {
    setPendingIndexes(prev => prev.filter(k => k !== key));
  };
  const handleHideAll = () => {
    setPendingIndexes([]);
  };
  const handleOrderChange = (fromIndex, toIndex) => {
    setPendingIndexes(prev => reorderList(prev, fromIndex, toIndex));
  };
  const handleSave = () => {
    onSave(pendingIndexes);
  };
  const handleClose = () => {
    setPendingIndexes(addedColumnIndexes);
    setSearchTerm('');
    onClose();
  };
  const shownCustomCount = pendingIndexes.length;
  const hasShownSection = filteredStaticColumns.length > 0 || filteredPendingKeys.length > 0;
  const hasHiddenSection = filteredHiddenFields.length > 0;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    toggle: handleClose,
    size: "sm",
    className: "t-col-customizer-modal",
    "data-testid": "t-col-customizer-modal"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: handleClose,
    className: "border-0 pb-0"
  }, "Fields"), /*#__PURE__*/React.createElement(ModalBody, {
    className: "pt-2 pb-0"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "search",
    placeholder: "Search for fields",
    value: searchTerm,
    onChange: e => setSearchTerm(e.target.value),
    "data-testid": "t-col-customizer-search",
    className: "mb-3"
  }), /*#__PURE__*/React.createElement("div", {
    className: "t-col-customizer-scroll",
    style: {
      maxHeight: 'min(60vh, 420px)',
      overflowY: 'auto'
    }
  }, hasShownSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center justify-content-between px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Shown"), shownCustomCount > 0 && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-link btn-sm p-0 text-decoration-none",
    "data-testid": "t-col-customizer-hide-all",
    onClick: handleHideAll
  }, "Hide all")), filteredStaticColumns.map(col => /*#__PURE__*/React.createElement(StaticFieldRow, {
    key: col.title,
    title: col.title
  })), filteredPendingKeys.map(key => {
    var _fieldByKey$key2;
    const fullIndex = pendingIndexes.indexOf(key);
    const title = ((_fieldByKey$key2 = fieldByKey[key]) === null || _fieldByKey$key2 === void 0 ? void 0 : _fieldByKey$key2.title) || key;
    return /*#__PURE__*/React.createElement(CabinetColumnCustomizerDraggableRow, {
      key: key,
      fieldKey: key,
      title: title,
      index: fullIndex,
      onOrderChange: handleOrderChange,
      onToggleOff: handleToggleOff
    });
  })), hasShownSection && hasHiddenSection && /*#__PURE__*/React.createElement("hr", {
    className: "my-2"
  }), hasHiddenSection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "px-3 mb-1"
  }, /*#__PURE__*/React.createElement("span", {
    className: "small fw-semibold text-muted text-uppercase"
  }, "Hidden")), filteredHiddenFields.map(field => /*#__PURE__*/React.createElement(HiddenFieldRow, {
    key: field.key,
    field: field,
    onToggleOn: handleToggleOn
  }))), !hasShownSection && !hasHiddenSection && /*#__PURE__*/React.createElement("p", {
    className: "text-muted small px-3 mb-0"
  }, "No fields match your search."))), /*#__PURE__*/React.createElement(ModalFooter, {
    className: "border-0 pt-2"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    "data-testid": "t-col-customizer-done",
    onClick: handleSave
  }, "Done")));
};
CabinetColumnCustomizer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  staticColumns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired
  })).isRequired,
  candidateFields: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })).isRequired,
  addedColumnIndexes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
export default CabinetColumnCustomizer;